import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { serverTimestamp } from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { SchoolMember } from '../../../models/member.school.model';
import { MembershipStatus } from '../../../models/membership.status';
import * as memberActions from '../../../state/actions/member.school.actions';
import { AppState } from '../../../state/appstate';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { DlgAssignRolesComponent } from '../dlg-assign-roles/dlg-assign-roles.component';

@Component({
  selector: 'app-dlg-assign-member-status',
  standalone: true,
  imports: [
    MatFormFieldModule,
    BtnActionComponent,
    AsyncPipe,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    BtnSubmitComponent,
  ],
  templateUrl: './dlg-assign-member-status.component.html',
  styleUrl: './dlg-assign-member-status.component.css',
})
export class DlgAssignMemberStatusComponent {
  readonly dialogRef = inject(MatDialogRef<DlgAssignRolesComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);
  private actions$: Actions = inject(Actions);
  logger: NGXLogger = inject(NGXLogger);

  title: string;
  description: string = 'Select an option to proceed';
  action: string = 'Assign Member Status';
  options: MembershipStatus[] = [];
  selectedOption: string | null = null;

  working$: Observable<boolean> = of(false);
  exit$: Subject<void> = new Subject();

  constructor(private store: Store<AppState>) {
    this.title = this.data.title;
    this.description = this.data.description;
    this.action = this.data.action;
    this.selectedOption = this.data.member.status ?? null;

    this.options = [
      MembershipStatus.active,
      MembershipStatus.rejected,
      MembershipStatus.removed,
      MembershipStatus.alumni,
    ];
  }

  ngOnInit(): void {
    this.actions$
      .pipe(ofType(memberActions.updateStatus), takeUntil(this.exit$))
      .subscribe(({ id, member }) => {
        this.working$ = of(true);
      });

    this.actions$
      .pipe(ofType(memberActions.updateStatusSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.logger.debug('Member status updated successfully');
        this.working$ = of(false);
        this.dialogRef.close(true);
      });

    this.actions$
      .pipe(ofType(memberActions.updateStatusFailure), takeUntil(this.exit$))
      .subscribe(({ error }) => {
        this.logger.debug('Member status update failed, error: ', error);
        this.onShowAlert(error);
        this.working$ = of(false);
      });
  }

  onChangeOption(event: any): void {
    const value: MembershipStatus = event.target.value;
    this.selectedOption = value;
  }

  onTextSelection(value: MembershipStatus) {
    this.selectedOption = value;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (
      this.selectedOption == null ||
      this.selectedOption === this.data.member.status
    ) {
      return this.onCancel();
    }

    if (this.selectedOption === MembershipStatus.removed) {
      this.store.dispatch(
        memberActions.deleteAction({
          id: this.data.member.id!,
        }),
      );
      return this.onCancel();
    }

    this.store.dispatch(
      memberActions.updateStatus({
        id: this.data.member.id!,
        member: {
          ...this.data.member,
          status: this.selectedOption,
          modified: serverTimestamp(),
        } as SchoolMember,
      }),
    );
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
